import {
  loadDecisions,
  saveDecision,
  toast,
  money,
  tierClass,
  getReviewToken,
  setReviewToken,
} from "../api.js";

let _state = { cards: [], decisions: new Map(), filter: "all" };

function statusOf(card) {
  return _state.decisions.get(card.key)?.status || card.decision?.status || "pending";
}

function recount() {
  const c = _state.cards;
  const p = c.filter((x) => statusOf(x) === "pending").length;
  const m = c.filter((x) => statusOf(x) === "measure_ready").length;
  const r = c.filter((x) => statusOf(x) === "rejected").length;
  const el = document.getElementById("rev-stats");
  if (el) {
    el.innerHTML = `<span style="color:var(--yellow)">Pending ${p}</span>
      <span style="color:var(--green);margin-left:.75rem">Ready ${m}</span>
      <span style="color:var(--red);margin-left:.75rem">Rejected ${r}</span>`;
  }
}

function cardHtml(card) {
  const st = statusOf(card);
  const img = card.image || {};
  const rec = card.recommendation || {};
  const viv = card.viability || {};
  const demo = card.demo || {};
  const dec = _state.decisions.get(card.key) || {};
  let media;
  if (img.ok && img.path) {
    media = `<img src="${img.path}" alt="Street View ${card.address || ""}" loading="lazy"/>`;
  } else {
    media = `<div class="ph"><div>No Street View screenshot</div>
      <div class="muted">${img.reason || ""}</div>
      ${card.streetview_url ? `<a href="${card.streetview_url}" target="_blank" rel="noopener">Open Street View ↗</a>` : ""}
      ${card.maps_url ? `<a href="${card.maps_url}" target="_blank" rel="noopener">Open Maps ↗</a>` : ""}
    </div>`;
  }
  return `
    <article class="rev-card ${st}" data-key="${card.key}">
      <div class="img-wrap">${media}</div>
      <div class="rev-body">
        <div class="addr">${card.address || ""}</div>
        <div class="muted">${card.city || ""}, ${card.state || ""} ${card.zip || ""} · fit ${card.score ?? "—"}</div>
        <div>
          <span class="badge b-yellow">${st === "measure_ready" ? "Ready to measure" : st === "rejected" ? "Rejected" : "Needs look"}</span>
          ${rec.ladder_focus ? `<span class="badge b-blue">${String(rec.ladder_focus)}</span>` : ""}
          ${rec.primary_brand_name ? `<span class="badge b-green">${rec.primary_brand_name}</span>` : ""}
          ${card.tier ? `<span class="badge ${tierClass(card.tier)}">${card.tier}</span>` : ""}
        </div>
        <div class="muted">Lot ${(card.lot_sqft || 0).toLocaleString()} sqft${demo.median_hh_income ? ` · area income ${money(demo.median_hh_income)}` : ""}</div>
        <div class="muted">${rec.job_label || rec.primary_brand_name || ""}</div>
        <input class="reason" type="text" placeholder="Why reject? (optional)" value="${(dec.reason || "").replace(/"/g, "&quot;")}"/>
        <div class="actions">
          <button type="button" class="primary btn-ok">✓ Looks good — measure</button>
          <button type="button" class="danger btn-no">✗ Skip this one</button>
        </div>
        <div class="muted" style="font-size:.75rem;margin-top:.25rem">
          ${card.streetview_url ? `<a href="${card.streetview_url}" target="_blank" rel="noopener">SV</a> · ` : ""}
          ${card.maps_url ? `<a href="${card.maps_url}" target="_blank" rel="noopener">Maps</a>` : ""}
        </div>
      </div>
    </article>`;
}

function paintGrid() {
  const grid = document.getElementById("rev-grid");
  if (!grid) return;
  const f = _state.filter;
  const list = _state.cards.filter((c) => f === "all" || statusOf(c) === f);
  grid.innerHTML = list.map(cardHtml).join("") || `<p class="muted">No cards match filter</p>`;
  recount();
}

async function decide(card, status, reason) {
  const decision = {
    key: card.key,
    status,
    reason: reason || "",
    address: card.address || "",
    reviewer: "web",
    ts: new Date().toISOString(),
  };
  _state.decisions.set(card.key, decision);
  const res = await saveDecision(decision);
  paintGrid();
  if (res.remote) {
    toast(status === "rejected" ? `Skipped ${card.address}` : `Marked ready: ${card.address}`);
  } else {
    toast("Saved on this device", false);
  }
}

export async function renderReview(pack) {
  _state.cards = pack.review_cards || [];
  _state.decisions = await loadDecisions(pack);
  _state.filter = "all";

  // Apply decisions onto cards for initial paint
  for (const c of _state.cards) {
    const d = _state.decisions.get(c.key);
    if (d) c.decision = d;
  }

  return `
    <h1>Street View review</h1>
    <p class="sub">Quick look before you schedule a measure — skip apartments, parking lots, and commercial buildings</p>
    <aside class="help-tip" role="note">
      <div class="help-tip-title">How to review</div>
      <div class="help-tip-body">
        <ol class="help-list">
          <li>Open each photo — does this look like a home with outdoor living potential?</li>
          <li>Tap <strong>Looks good — measure</strong> if you would visit.</li>
          <li>Tap <strong>Skip this one</strong> for multi-family, wrong property, or no outdoor space.</li>
        </ol>
      </div>
    </aside>
    <div class="toolbar">
      <div id="rev-stats"></div>
      <select id="rev-filter">
        <option value="all">Show all</option>
        <option value="pending">Still need a look</option>
        <option value="measure_ready">Ready to measure</option>
        <option value="rejected">Skipped</option>
      </select>
      <button type="button" id="btn-export-rev">Download decisions</button>
    </div>
    <div class="rev-grid" id="rev-grid"></div>
  `;
}

export function mountReview(pack) {
  paintGrid();
  const filter = document.getElementById("rev-filter");
  if (filter) {
    filter.onchange = () => {
      _state.filter = filter.value;
      paintGrid();
    };
  }
  const tok = document.getElementById("rev-token");
  if (tok) {
    tok.onchange = () => setReviewToken(tok.value.trim());
  }
  const exp = document.getElementById("btn-export-rev");
  if (exp) {
    exp.onclick = () => {
      const decisions = [..._state.decisions.values()];
      const blob = new Blob([JSON.stringify({ decisions }, null, 2)], { type: "application/json" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = "leadforge-reviews.json";
      a.click();
    };
  }
  const grid = document.getElementById("rev-grid");
  if (grid) {
    grid.onclick = async (e) => {
      const btn = e.target.closest("button");
      if (!btn) return;
      const article = e.target.closest(".rev-card");
      if (!article) return;
      const key = article.dataset.key;
      const card = _state.cards.find((c) => c.key === key);
      if (!card) return;
      const reason = article.querySelector(".reason")?.value || "";
      if (btn.classList.contains("btn-ok")) await decide(card, "measure_ready", reason);
      if (btn.classList.contains("btn-no")) await decide(card, "rejected", reason || "manual_reject");
    };
  }
}
